import { Injectable, inject } from '@angular/core';
import { IrisUserService } from '@iris/common/services/user.service';
import { isEmpty } from 'lodash';
import { IrisStorage } from './storage';

@Injectable({
  providedIn: 'root',
})
export class IrisLocalStorage implements IrisStorage {
  private readonly userService = inject(IrisUserService);

  getItem<T>(key: string): T | null {
    const item = localStorage.getItem(this.getLocalStorageKey(key));

    try {
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  }

  /**
   * Get int value inside locale storage.
   * @param key
   * @param deleteIfNotNumber
   * @returns null if no value can be found in locale storage, or found value is not a number
   */
  getInt(key: string, deleteIfNotNumber = false): number | null {
    const item = localStorage.getItem(this.getLocalStorageKey(key));

    if (typeof item === 'string') {
      const parsedItem = parseInt(item);
      if (!!item && isNaN(parsedItem) && deleteIfNotNumber) {
        // If item is present, but applications expects a number, delete the old value (if requested)
        this.removeItem(key);
        return null;
      }

      return parsedItem;
    } else if (typeof item === 'number') {
      return item;
    }

    return null;
  }

  setItem<T>(key: string, value: T): void {
    if (typeof value === 'number' || typeof value === 'boolean') {
      localStorage.setItem(this.getLocalStorageKey(key), `${value}`);
    } else if (isEmpty(value)) {
      this.removeItem(key);
    } else {
      localStorage.setItem(this.getLocalStorageKey(key), JSON.stringify(value));
    }
  }

  removeItem(key: string): void {
    localStorage.removeItem(this.getLocalStorageKey(key));
  }

  private getLocalStorageKey(key: string): string {
    const userId = this.userService.me?.id || 'anonymous';
    return `${userId}_${key}`;
  }
}
